import {
  CalendarDays,
  ClipboardList,
  FileSignature,
  Pill,
  ScanLine,
  Smile,
  Send,
  Users,
  History,
  Lock,
  Cloud,
  MonitorSmartphone,
} from 'lucide-react';

// Palabras que rotan en el titular del hero.
export const HERO_WORDS = ['agenda', 'ficha clínica', 'receta', 'firma'];

// Recorrido por funciones (pestañas). `mock` es la clave de la pantalla simulada en LandingMocks.
export const FEATURE_TABS = [
  {
    id: 'agenda',
    icon: CalendarDays,
    label: 'Agenda',
    title: 'Una agenda que ordena el día de todo el equipo',
    text: 'Agenda por profesional y box, con horarios configurables. Recepción agenda, confirma y ve la disponibilidad real en segundos.',
    bullets: ['Vista por profesional y por box', 'Horarios y bloques configurables', 'Citas en pocos clics'],
    mock: 'agenda',
  },
  {
    id: 'odontograma',
    icon: Smile,
    label: 'Odontograma',
    title: 'Odontograma completo, pieza por pieza',
    text: 'Registra hallazgos por pieza y superficie con nomenclatura FDI, y sigue la evolución del paciente en cada control.',
    bullets: ['Dentición permanente y temporal', 'Hallazgos por superficie', 'Tabla de hallazgos y evolución'],
    mock: 'odontograma',
  },
  {
    id: 'ficha',
    icon: ClipboardList,
    label: 'Ficha clínica',
    title: 'La ficha del paciente, completa y en un solo lugar',
    text: 'Historial, antecedentes, atenciones y documentos del paciente. Plantillas de ficha a la medida de tu especialidad.',
    bullets: ['Plantillas por especialidad', 'Historial clínico completo', 'Digitaliza formularios en papel'],
    mock: 'ficha',
  },
  {
    id: 'consentimientos',
    icon: FileSignature,
    label: 'Consentimientos',
    title: 'Consentimientos firmados en pantalla',
    text: 'El paciente firma en la tablet o el computador y el documento queda guardado en su ficha. Sin imprimir ni escanear.',
    bullets: ['Firma digital en pantalla', 'Guardado automático en la ficha', 'Plantillas reutilizables'],
    mock: 'consentimiento',
  },
  {
    id: 'recetas',
    icon: Pill,
    label: 'Recetas y órdenes',
    title: 'Recetas y órdenes de examen en segundos',
    text: 'Maestro de medicamentos y exámenes de tu clínica para emitir recetas y órdenes sin escribir lo mismo una y otra vez.',
    bullets: ['Catálogo de medicamentos', 'Catálogo de exámenes', 'Documentos listos para entregar'],
    mock: 'receta',
  },
  {
    id: 'rx',
    icon: ScanLine,
    label: 'Informes RX',
    title: 'Radiografías e informes, junto al paciente',
    text: 'Sube radiografías, compáralas entre controles y genera informes radiológicos y de derivación con los datos ya cargados.',
    bullets: ['Comparación entre controles', 'Informes radiológicos', 'Informes de derivación'],
    mock: 'rx',
  },
];

// Slides del hero: reutilizan las pantallas simuladas.
export const HERO_SLIDES = [
  { mock: 'agenda', caption: 'Agenda del día por profesional y box' },
  { mock: 'odontograma', caption: 'Odontograma con hallazgos por pieza' },
  { mock: 'ficha', caption: 'Ficha clínica e historial del paciente' },
  { mock: 'consentimiento', caption: 'Consentimiento firmado en pantalla' },
];

// Cifras del hero: solo datos reales de la oferta.
export const STATS = [
  { value: 48, suffix: ' h', label: 'para dejar tu clínica funcionando' },
  { value: 30, suffix: ' días', label: 'de piloto acompañado' },
  { value: 20, prefix: '+', label: 'especialidades en el catálogo' },
  { value: 0, label: 'programas que instalar' },
];

export const BEFORE_AFTER = [
  { before: 'Agenda en cuaderno o planilla, y confirmaciones una por una.', after: 'Agenda compartida por profesional y box, disponible para todo el equipo.' },
  { before: 'Fichas en papel que se traspapelan o cuesta entender.', after: 'Ficha digital con el historial completo del paciente, siempre a mano.' },
  { before: 'Consentimientos impresos, firmados y escaneados.', after: 'Firma en pantalla que queda guardada en la ficha.' },
  { before: 'Recetas escritas a mano, una y otra vez.', after: 'Recetas y órdenes desde el catálogo de tu clínica, en segundos.' },
  { before: 'Información repartida entre WhatsApp, Excel y carpetas.', after: 'Todo en un solo sistema, con permisos para cada rol.' },
];

// Comparativa: true = sí, false = no, texto = parcial.
export const COMPARISON = [
  { feature: 'Agenda compartida en tiempo real', paper: 'Parcial', medix: true },
  { feature: 'Historial clínico completo y ordenado', paper: false, medix: true },
  { feature: 'Firma digital de consentimientos', paper: false, medix: true },
  { feature: 'Recetas y órdenes desde catálogo', paper: false, medix: true },
  { feature: 'Odontograma digital por pieza', paper: false, medix: true },
  { feature: 'Permisos por rol para el equipo', paper: false, medix: true },
  { feature: 'Registro de quién hizo qué y cuándo', paper: false, medix: true },
  { feature: 'Acceso desde computador o tablet', paper: 'Parcial', medix: true },
  { feature: 'Respaldo en la nube', paper: false, medix: true },
];

export const SPECIALTIES = [
  'Odontología', 'Imagenología', 'Medicina general', 'Pediatría', 'Ginecología y obstetricia',
  'Cardiología', 'Dermatología', 'Traumatología', 'Neurología', 'Psiquiatría', 'Psicología clínica',
  'Nutrición', 'Kinesiología', 'Fonoaudiología', 'Oftalmología', 'Otorrinolaringología', 'Urología',
  'Endocrinología', 'Gastroenterología', 'Reumatología', 'Geriatría', 'Medicina del deporte', 'Medicina estética',
];

export const TRUST = [
  { icon: Lock, title: 'Roles y permisos', text: 'Cada persona ve y hace solo lo que le corresponde: recepción, profesionales, administración.' },
  { icon: History, title: 'Auditoría', text: 'Registro de quién hizo qué y cuándo, para tener trazabilidad de cada cambio.' },
  { icon: Cloud, title: 'Datos en Chile', text: 'Base de datos alojada en Google Cloud, región Santiago de Chile.' },
  { icon: MonitorSmartphone, title: 'Sin instalar nada', text: 'Funciona en el navegador del computador o la tablet. Siempre actualizado.' },
  { icon: Users, title: 'Todo el equipo conectado', text: 'Recepción, profesionales y administración trabajando sobre la misma información.' },
  { icon: Send, title: 'Derivaciones listas', text: 'Informes de derivación con los antecedentes del paciente ya cargados.' },
];

// Precios en CLP, más IVA. Fuente: go-to-market/chile-90d/01_pricing_y_oferta.md
export const PLANS = [
  {
    name: 'Solo',
    audience: 'Odontólogo o profesional independiente',
    monthly: 39900,
    included: 1,
    extraUser: null,
    setup: 0,
    pilot: 19900,
  },
  {
    name: 'Clínica Start',
    audience: 'Clínicas pequeñas, de 2 a 4 personas',
    monthly: 119900,
    included: 4,
    extraUser: 14900,
    setup: 49900,
    pilot: 49900,
    featured: true,
  },
  {
    name: 'Clínica Growth',
    audience: 'Clínicas medianas, de 5 a 10 personas',
    monthly: 219900,
    included: 10,
    extraUser: 12900,
    setup: 89900,
    pilot: 79900,
  },
];

// Pago anual: 2 meses de descuento (se pagan 10 de 12).
export const ANNUAL_MONTHS_PAID = 10;

export const PILOT_STEPS = [
  { when: 'Día 1–2', title: 'Implementación en 48 horas', text: 'Configuramos contigo la clínica, los usuarios y la agenda base.' },
  { when: 'Día 3–30', title: 'Uso real con tu equipo', text: 'Trabajan con Medix en el día a día, con soporte prioritario por WhatsApp y email.' },
  { when: 'Día 21', title: 'Revisión de resultados', text: 'Revisamos juntos cómo va. Si continúas, el valor del piloto se descuenta del primer mes.' },
];

export const FAQ = [
  {
    q: '¿Necesito instalar algo?',
    a: 'No. Medix funciona en el navegador, desde el computador o la tablet. Solo necesitas conexión a internet.',
  },
  {
    q: '¿Dónde quedan guardados los datos de mis pacientes?',
    a: 'En Google Cloud, en la región de Santiago de Chile. El acceso se controla por roles y queda registro de auditoría de los cambios.',
  },
  {
    q: '¿Cuánto demora empezar?',
    a: 'La implementación toma 48 horas: configuramos contigo la clínica, los usuarios y la agenda.',
  },
  {
    q: '¿Cómo funciona el piloto de 30 días?',
    a: 'Es un piloto pagado con implementación, soporte prioritario y una revisión de resultados el día 21. Si decides continuar, el valor del piloto se descuenta del primer mes.',
  },
  {
    q: '¿Tengo que firmar un contrato largo?',
    a: 'No. El plan mensual es sin permanencia. Si prefieres pagar anual, tienes 2 meses de descuento.',
  },
  {
    q: '¿Los precios incluyen IVA?',
    a: 'No, los precios publicados son mensuales, en pesos chilenos, más IVA.',
  },
  {
    q: '¿Sirve para mi especialidad?',
    a: 'La base (agenda, pacientes, fichas, consentimientos, recetas) sirve para cualquier especialidad, y Medix suma módulos específicos, como el odontograma en odontología. Escríbenos y te contamos qué hay para la tuya.',
  },
];
